import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import { ensureCurrentUser } from '../../util/data';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import * as validators from '../../util/validators';
import {
  Form,
  PrimaryButton,
  SecondaryButton,
  FieldTextInput,
  FieldSelect,
  UserSearchInput,
} from '../../components';
import { connect } from 'react-redux';
import { searchUsers, getSearchResults, getSearchInProgress } from '../../ducks/userSearch.duck';

import css from './InviteTeamMemberForm.module.css';

const START_YEAR = 1950;
const END_YEAR = new Date().getFullYear();

const generateYearsArray = (startYear = START_YEAR, endYear = END_YEAR) => {
  const years = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push({ key: year.toString(), label: year.toString() });
  }
  return years;
};

const InviteTeamMemberFormComponent = props => {
  const { onSearchUsers, searchResults, searchInProgress, currentListing, ...formProps } = props;

  return (
    <FinalForm
      {...formProps}
      render={fieldRenderProps => {
        const {
          className,
          rootClassName,
          disabled,
          handleSubmit,
          intl,
          formId,
          invalid,
          values,
          currentUser,
          team,
          inviteSent,
          sendInviteError,
          sendInviteInProgress,
          creditRolesConfigOptions,
          form,
          onCancel,
        } = fieldRenderProps;

        // Handle user selection from search
        const handleUserSelect = selectedUser => {
          // Store userId for existing users (no email needed)
          const userIdFieldName = formId ? `${formId}.userId` : 'userId';
          const emailFieldName = formId ? `${formId}.email` : 'email';

          form.change(userIdFieldName, selectedUser.id.uuid);
          form.change(emailFieldName, null); // Clear email field
        };

        // Handle direct email input
        const handleEmailInput = email => {
          const emailFieldName = formId ? `${formId}.email` : 'email';
          const userIdFieldName = formId ? `${formId}.userId` : 'userId';

          form.change(emailFieldName, email);
          form.change(userIdFieldName, null); // Clear userId field
        };

        const ensuredCurrentUser = ensureCurrentUser(currentUser);
        const { email } = ensuredCurrentUser.attributes;

        const invitedUserEmails = team?.map(u => u.attributes.email);

        // email
        const emailLabel = intl.formatMessage({
          id: 'InviteTeamMemberForm.emailLabel',
        });
        const emailPlaceholder = intl.formatMessage({
          id: 'InviteTeamMemberForm.emailPlaceholder',
        });
        const emailRequiredMessage = intl.formatMessage({
          id: 'InviteTeamMemberForm.emailRequired',
        });
        const emailRequired = validators.required(emailRequiredMessage);
        const emailInvalidMessage = intl.formatMessage({
          id: 'InviteTeamMemberForm.emailInvalid',
        });
        const emailValid = validators.emailFormatValid(emailInvalidMessage);
        const ownEmailMessage = intl.formatMessage({
          id: 'InviteTeamMemberForm.ownEmail',
        });
        const ownEmailValidation = validators.ownEmail(ownEmailMessage, email);
        const uniqueEmailMessage = intl.formatMessage({
          id: 'InviteTeamMemberForm.uniqueEmail',
        });
        const uniqueEmailValidation = validators.uniqueEmail(uniqueEmailMessage, invitedUserEmails);

        // Credit
        const creditLabel = intl.formatMessage({
          id: 'InviteTeamMemberForm.creditLabel',
        });
        const creditPlaceholder = intl.formatMessage({
          id: 'InviteTeamMemberForm.creditPlaceholder',
        });
        const creditRequiredMessage = intl.formatMessage({
          id: 'InviteTeamMemberForm.creditRequired',
        });

        // Start year
        const startYearLabel = intl.formatMessage({
          id: 'InviteTeamMemberForm.startYear',
        });
        const startYearPlaceholder = intl.formatMessage({
          id: 'InviteTeamMemberForm.startYearPlaceholder',
        });
        const startYearRequiredMessage = intl.formatMessage({
          id: 'InviteTeamMemberForm.startYearRequired',
        });

        // End year
        const endYearLabel = intl.formatMessage({
          id: 'InviteTeamMemberForm.endYear',
        });
        const endYearPlaceholder = intl.formatMessage({
          id: 'InviteTeamMemberForm.endYearPlaceholder',
        });
        const endYearRequiredMessage = intl.formatMessage({
          id: 'InviteTeamMemberForm.endYearRequired',
        });

        const errorMessage = (
          <p className={css.error}>
            <FormattedMessage id="InviteTeamMemberForm.inviteSubmitFailed" />
          </p>
        );
        const errorArea = sendInviteError ? errorMessage : null;

        const inviteSubmitMessage = intl.formatMessage({
          id: 'InviteTeamMemberForm.inviteSubmit',
        });

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = sendInviteInProgress;
        const submitDisabled = invalid || disabled || submitInProgress;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div>
              <div className={css.inputWrapper}>
                <UserSearchInput
                  id={formId ? `${formId}.search` : 'search'}
                  label={emailLabel}
                  onUserSelect={handleUserSelect}
                  onEmailInput={handleEmailInput}
                  onSearchUsers={onSearchUsers}
                  searchResults={searchResults}
                  searchInProgress={searchInProgress}
                  placeholder={emailPlaceholder}
                  autoFocus
                />
                {/* Hidden fields to store the actual values for form submission */}
                <FieldTextInput
                  type="hidden"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  validate={
                    values?.userId
                      ? null
                      : validators.composeValidators(
                          emailRequired,
                          emailValid,
                          ownEmailValidation,
                          uniqueEmailValidation
                        )
                  }
                />
                <FieldTextInput
                  type="hidden"
                  id={formId ? `${formId}.userId` : 'userId'}
                  name="userId"
                />
              </div>
            </div>

            <FieldSelect
              id={formId ? `${formId}.credit` : 'credit'}
              name={formId ? `${formId}.credit` : 'credit'}
              label={creditLabel}
              validate={validators.required(creditRequiredMessage)}
            >
              <option value="" disabled>
                {creditPlaceholder}
              </option>
              {creditRolesConfigOptions.map(o => {
                return (
                  <option key={o.value} value={o.label}>
                    {o.label}
                  </option>
                );
              })}
            </FieldSelect>

            <div className={css.yearFields}>
              <FieldSelect
                className={css.yearField}
                id={formId ? `${formId}.year` : 'year'}
                name={formId ? `${formId}.year.start` : 'year.start'}
                label={startYearLabel}
                validate={validators.required(startYearRequiredMessage)}
              >
                <option disabled value="">
                  {startYearPlaceholder}
                </option>

                {generateYearsArray(START_YEAR, END_YEAR).map(year => (
                  <option key={year.key} value={year.key}>
                    {year.label}
                  </option>
                ))}
              </FieldSelect>

              <FieldSelect
                className={css.yearField}
                id={formId ? `${formId}.end` : 'end'}
                name={formId ? `${formId}.year.end` : 'year.end'}
                label={endYearLabel}
                validate={validators.required(endYearRequiredMessage)}
              >
                <option disabled value="">
                  {endYearPlaceholder}
                </option>

                {generateYearsArray(START_YEAR, END_YEAR).map(year => (
                  <option
                    key={year.key}
                    value={year.key}
                    disabled={parseInt(year.key) < parseInt(values?.year?.start)}
                  >
                    {year.label}
                  </option>
                ))}
                <option key="ongoing" value="ongoing">
                  {intl.formatMessage({ id: 'InviteTeamMemberForm.ongoing' })}
                </option>
              </FieldSelect>
            </div>

            {errorArea}
            <div className={css.footer}>
              {onCancel ? (
                <SecondaryButton type="button" className={css.footerButton} onClick={onCancel}>
                  <FormattedMessage id="InviteTeamMemberForm.cancel" />
                </SecondaryButton>
              ) : null}
              <PrimaryButton
                className={css.footerButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={inviteSent}
              >
                {inviteSubmitMessage}
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

InviteTeamMemberFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
  sendInviteError: null,
  onCancel: null,
};

const { bool, func, string, arrayOf } = PropTypes;

InviteTeamMemberFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  // Shows a Cancel button (the modal passes its close)
  onCancel: func,

  currentUser: propTypes.currentUser,
  currentListing: PropTypes.oneOfType([propTypes.listing, propTypes.ownListing]),

  pendingInviteUsers: arrayOf(propTypes.user),
  sendInviteError: propTypes.error,
  sendInviteInProgress: bool.isRequired,

  // User search props
  onSearchUsers: func.isRequired,
  searchResults: arrayOf(propTypes.user),
  searchInProgress: bool,
};

// Redux connection for user search
const mapStateToProps = state => ({
  searchResults: getSearchResults(state),
  searchInProgress: getSearchInProgress(state),
});

const mapDispatchToProps = dispatch => ({
  onSearchUsers: (searchTerm, options) => dispatch(searchUsers(searchTerm, options)),
});

const InviteTeamMemberForm = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(InviteTeamMemberFormComponent);
InviteTeamMemberForm.displayName = 'InviteTeamMemberForm';

export default InviteTeamMemberForm;
